'use client'

import React from 'react'
import Footer from '@/components/Footer'
import EarlyAccessForm from './EarlyAccessForm'

export default function AwardPlexPage() {
  // Simple analytics helper
  const track = (eventName: string, data?: Record<string, any>) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('event', eventName, data)
    }
  }

  const scrollToForm = () => {
    document.getElementById('early-access-form')?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToDemo = () => {
    document.getElementById('early-access-form')?.scrollIntoView({ behavior: 'smooth' })
    // Focus on form after scroll
    setTimeout(() => {
      const notesField = document.getElementById('notes') as HTMLTextAreaElement
      if (notesField) {
        notesField.focus()
        notesField.value = 'I would like to request a demo.'
        notesField.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }, 500)
  }

  return (
    <>
      <div className="min-h-screen bg-[#0A0E1A] text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-6 sm:px-8 overflow-visible">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="space-y-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30">
              <span className="text-xs font-mono text-blue-400 uppercase tracking-wider">Early Access</span>
            </div>

            {/* H1 */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-light leading-[1.1] tracking-tight">
              Replace forms & spreadsheets with a real awards workflow.
            </h1>

            {/* Subhead */}
            <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-3xl">
              AwardPlex helps you run nominations, judging, and committee oversight in one structured system — with role-based access, scoring criteria, deadlines, and full audit trails.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={() => {
                  track('awardplex_cta_early_access_click')
                  scrollToForm()
                }}
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-[16px] font-medium transition-colors text-base shadow-lg shadow-blue-500/20"
              >
                Get Early Access
              </button>
              <button
                onClick={() => {
                  track('awardplex_cta_demo_click')
                  scrollToDemo()
                }}
                className="px-8 py-4 border border-white/20 hover:border-white/40 text-white rounded-[16px] font-medium transition-colors text-base bg-white/5"
              >
                Request a Demo
              </button>
            </div>

            {/* Micro-trust line */}
            <p className="text-xs text-gray-500 font-mono pt-2">
              If your awards process involves forms + spreadsheets + anxiety… this is for you.
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-6">
            Forms and spreadsheets work… until they don't.
          </h2>
          <p className="text-lg text-gray-400 mb-12">
            They collect data. They don't run a fair awards process.
          </p>
          <ul className="space-y-4 text-gray-300">
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Responses land in spreadsheets. The workflow lives in Slack and email.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>No role separation: committee vs judges vs organizers all see everything.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Deadlines, cutoffs, and overrides require manual policing.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Fairness and audit trails are hard to enforce, harder to prove.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Publishing results safely becomes a scramble.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Why AwardPlex Wins Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">
              Why teams switch to AwardPlex
            </h2>
            <p className="text-lg text-gray-400">
              A premium awards experience — without the enterprise friction.
            </p>
          </div>

          {/* Three comparison cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Card 1 */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">Faster than forms + spreadsheets</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>One structured workflow from submission to results</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Roles: organizers, committee, judges — properly separated</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Scoring criteria, cutoffs, and visibility built in</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Audit trails so decisions are defensible</span>
                </li>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">Easier than enterprise tools</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Self-onboarding — no calls, no setup workshops</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Designed for zero learning curve</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Runs smoothly for both small and large programs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>A clean reviewer experience judges actually enjoy</span>
                </li>
              </ul>
            </div>

            {/* Card 3 */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">Pay per award cycle, not per month</h3>
              <ul className="space-y-3 text-sm text-gray-400 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Pay by submission volume</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Top up when needed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Premium experience at reasonable cost</span>
                </li>
              </ul>
              <p className="text-xs text-gray-500 italic mt-4 pt-4 border-t border-white/5">
                Early access users get first access to cycle-based pricing.
              </p>
            </div>
          </div>

          {/* Proof micro-row */}
          <div className="flex flex-wrap justify-center gap-3">
            {['Setup in minutes', 'Low learning curve', 'Built for fairness', 'Audit-ready'].map((item, idx) => (
              <span
                key={idx}
                className="px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-mono"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Simple Setup Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-12 text-center">
            Setup is genuinely simple
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <span className="text-blue-400 mt-1 text-xl">1</span>
              <div>
                <p className="text-gray-300">Create a program → add categories → set criteria</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-400 mt-1 text-xl">2</span>
              <div>
                <p className="text-gray-300">Collect submissions (structured form) or import CSV</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-400 mt-1 text-xl">3</span>
              <div>
                <p className="text-gray-300">Invite judges/committee → assign → review</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-400 mt-1 text-xl">4</span>
              <div>
                <p className="text-gray-300">Publish results with controlled visibility</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/30">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* For Organizers */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">For Organizers</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Create programs, categories, timelines</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Import nominees via CSV or use structured forms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Manage participants & communications</span>
                </li>
              </ul>
            </div>

            {/* For Committee */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">For Committee</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Approvals + hold/disqualify controls</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Score visibility + outlier invalidation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Audit trail of actions & changes</span>
                </li>
              </ul>
            </div>

            {/* For Judges */}
            <div className="p-6 rounded-[20px] border border-white/10 bg-white/5">
              <h3 className="text-xl font-medium mb-4">For Judges</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Clean queue of assigned entries</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Criteria-based scoring + comments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Optional blind mode (hide nominee identity)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bias & Fairness Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-6">
            Fairness, by design.
          </h2>
          <p className="text-lg text-gray-400 mb-12">
            Built for awards where credibility, trust, and defensibility matter.
          </p>
          <ul className="space-y-4 text-gray-300">
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Blind reviews (optional)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Committee-only personal info visibility</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Review cutoffs + per-judge overrides</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Invalidate biased/outlier reviews</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span>Action logs for traceability</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-12 text-center">
            Use cases
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Internal company awards',
              'Employee recognition',
              'Partner/vendor awards',
              'Industry award programs',
              'Startup demo days / accelerators',
            ].map((useCase, idx) => (
              <div
                key={idx}
                className="p-6 rounded-[16px] border border-white/10 bg-white/5 text-center hover:border-blue-500/30 hover:bg-white/10 transition-all duration-200"
              >
                <p className="text-sm font-medium">{useCase}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Early Access Form */}
      <section id="early-access-form" className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">
              Get early access
            </h2>
            <p className="text-lg text-gray-400 mb-2">
              Tell us about your awards program — we'll reach out with a demo + early-access pricing.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              We're onboarding a limited number of award programs during early access.
            </p>
          </div>
          <EarlyAccessForm />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-12 text-center">
            FAQ
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'Can we do email-verified voting?',
                a: 'Yes. AwardPlex supports email-verified voting to ensure only authorized participants can submit nominations and scores.',
              },
              {
                q: 'Can we import from Google Sheets/CSV?',
                a: 'Yes. You can import nominees, judges, and existing data via CSV to get started quickly.',
              },
              {
                q: 'Do we need a setup call?',
                a: "No — AwardPlex is built for self-onboarding. If you want help, we can support you, but it's not required.",
              },
              {
                q: 'How does pricing work?',
                a: 'AwardPlex is designed around award cycles. Pricing can be based on submissions per cycle, with the ability to top up when needed. Early access users get priority access to cycle-based plans.',
              },
              {
                q: 'Can judges be restricted to categories?',
                a: 'Yes. Judge assignment is flexible — you can assign judges to specific categories or groups, ensuring they only see relevant entries.',
              },
              {
                q: 'Can we hide nominee details for unbiased scoring?',
                a: 'Yes. Blind review mode is available, allowing judges to score entries without seeing nominee identities.',
              },
              {
                q: "Can committee invalidate a judge's review?",
                a: 'Yes. Committee members have oversight controls to invalidate biased or outlier reviews, with full audit trail.',
              },
              {
                q: 'Hosted or self-hosted?',
                a: 'Early access is hosted. Self-hosting options will be available later.',
              },
            ].map((faq, idx) => (
              <details
                key={idx}
                className="p-6 rounded-[20px] border border-white/10 bg-white/5 group hover:border-blue-500/30 transition-all duration-200"
              >
                <summary className="text-lg font-medium cursor-pointer list-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded-[12px] p-2 -m-2">
                  <span className="flex items-center justify-between">
                    {faq.q}
                    <span className="text-blue-400 ml-4 group-open:hidden">+</span>
                    <span className="text-blue-400 ml-4 hidden group-open:inline">−</span>
                  </span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA Strip */}
      <section className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light">
            Stop running awards in spreadsheets. Run them with confidence.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                track('awardplex_cta_early_access_click')
                scrollToForm()
              }}
              className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-[16px] font-medium transition-colors text-base shadow-lg shadow-blue-500/20"
            >
              Get Early Access
            </button>
            <button
              onClick={() => {
                track('awardplex_cta_demo_click')
                scrollToDemo()
              }}
              className="px-8 py-4 border border-white/20 hover:border-white/40 text-white rounded-[16px] font-medium transition-colors text-base bg-white/5"
            >
              Request a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      </div>
    </>
  )
}

